import useSetExport from '@components/export/useSetExport';
import { cloneDeep } from 'lodash';
import { intervalAtom, usageMetricList, usageTableMetricList } from '../atoms';
import { useAtomValue } from 'jotai';
import { TOTAL_BILLABLE_HEADER_DISPLAY_NAME } from '@components/usage-dashboard/constant';
import {
  useFetchAPIUsageMetric,
  useFetchAppUsageMetric,
  useFetchVideoUsageMetric,
} from './dataFetcherhooks';
import {
  buildSeriesData,
  generateMarkLine,
  getUsageMetricsMeta,
  isNoPlanLimitMetric,
  isPlanLimitZero,
} from './utils';

const metricSeriesApisByProduct = {
  EI: (object) => useFetchVideoUsageMetric(object),
  APP: (object) => useFetchAppUsageMetric(object),
  API: (object) => useFetchAPIUsageMetric(object),
};

export function useGetDataForMetric(metric) {
  const product = usageMetricList[metric].product;
  return metricSeriesApisByProduct[product]({ enabled: false, product, metric });
}

export function useGetPlanLimitForMetric(metric) {
  const product = usageMetricList[metric].product;
  return metricSeriesApisByProduct[product]({ enabled: true, product, metric, isPlanLimit: true });
}

export function useMetricSeries({ metric, instance }: any) {
  const interval = useAtomValue(intervalAtom);
  const { data }: any = useGetDataForMetric(metric);
  const { data: planLimitData } = useGetPlanLimitForMetric(metric);
  const { data: chartData } = data;
  const markLine =
    isNoPlanLimitMetric(metric) || isPlanLimitZero(planLimitData)
      ? {}
      : generateMarkLine(planLimitData?.data, metric, chartData);

  if (instance) {
    buildSeriesData({
      instance,
      data: chartData,
      allMetrics: cloneDeep(getUsageMetricsMeta()),
      metric,
      markLine,
      interval,
    });
  }

  return chartData;
}

export function useMakeExportData({ seriesData = { data: [], totals: {} }, sectionKey, title }) {
  const timestampMap = {};
  let data: any = [];
  const header = ['Timestamp'];
  const footer = ['Total'];

  const timeseriesData = { ...seriesData.data };
  const totals = { ...seriesData.totals };

  // make empty hashmap like:-  { timestamp: [] }
  Object.keys(timeseriesData).forEach((metric) => {
    timeseriesData[metric].map((series) => {
      timestampMap[series[0]] = [];
    });
  });

  // make export data for every metric (put value into hashmap)
  Object.keys(timeseriesData).forEach((metric) => {
    header.push(usageMetricList[metric].displayName);
    timeseriesData[metric].map((series) => {
      timestampMap[series[0]].push(series[1]);
    });
    footer.push(totals[metric]);
  });

  Object.keys(timestampMap).forEach((timestamp) => {
    data.push([timestamp, ...timestampMap[timestamp]]);
  });
  data = [header, ...data, footer];

  useSetExport({
    pageKey: 'usageDashboard',
    sectionKey,
    widgetKey: 'metric',
    widgetInfo: { title },
    data,
    headers: [],
  });
}

// Builds the per-c3-account (group-by) table breakdown for export. Values are
// kept as raw (untruncated) numbers, unlike the humanized on-screen table.
export function useMakeTableExportData({
  tableData = { data: [], totals: {} },
  accountDetails = [],
  sectionKey,
  title,
}: any) {
  const usageMetricsMeta = getUsageMetricsMeta(usageTableMetricList);
  const columnKeys = Object.keys(tableData?.totals || {});

  const toRow = (name, item = {}) => [
    name,
    ...columnKeys.map((key) => {
      let value = item?.[key];
      if (value === undefined || value === null) {
        return '';
      }
      // mirror the on-screen table's seconds -> minutes conversion, but keep the
      // full precision instead of humanizing/truncating.
      if (usageMetricsMeta[key]?.unit === 'mins') {
        value = value / 60;
      }
      return value;
    }),
  ];

  const header = [
    'Account Name',
    ...columnKeys.map((key) => usageMetricsMeta[key]?.displayName ?? key),
  ];

  const rows = (tableData?.data ?? []).map((item) => {
    const match = accountDetails.find(
      (account) => String(account.customerId) === String(item.customer_id),
    );
    return toRow(match ? match.customerName : 'Unknown', item);
  });

  const data = columnKeys.length
    ? [header, toRow(TOTAL_BILLABLE_HEADER_DISPLAY_NAME, tableData?.totals || {}), ...rows]
    : [];

  useSetExport({
    pageKey: 'usageDashboard',
    sectionKey,
    widgetKey: 'metric',
    widgetInfo: { title },
    data,
    headers: [],
  });
}
